import { Collapse as MantineCollapse } from '@mantine/core';
import { memo, useCallback, useState } from 'react';
import { forwardRef } from 'react';
import { useTheme } from 'styled-components';

import { Icon, Text } from '../../ui';
import {
  StyledChevronWrapper,
  StyledContent,
  StyledContentWrapper,
  StyledIcon,
  StyledIconFolder,
  StyledIconWrapper,
  StyledLabel,
  StyledTextWrapper,
  StyledWrapper,
} from './Folder.style';

export { StyledListWrapper } from './Folder.style';

export interface FolderProps {
  name: string;
  subTitle?: string;
  children?: React.ReactNode;
  variant: 'collapse' | 'navigate';
  icon?: string;
  folderStyle?: 'default' | 'icon';
  indicator?: boolean;
  onClick?: () => void;
}

export const FolderIcon = memo(function FolderIcon({
  color,
  isOpened,
}: {
  color: string;
  isOpened: boolean;
}) {
  return <Icon name={'chevron---down'} color={color} size={'Medium'} angle={isOpened ? 180 : 0} />;
});

export const Folder = memo(
  forwardRef<HTMLDivElement, FolderProps>(function Folder(
    {
      name,
      children,
      variant = 'collapse',
      folderStyle,
      icon,
      indicator = true,
      subTitle,
      onClick,
    }: FolderProps,
    ref
  ) {
    const theme = useTheme();
    const [isOpened, setIsOpened] = useState(false);

    const handleOnChange = useCallback(() => {
      if (variant === 'collapse') {
        setIsOpened((prev) => !prev);
      }
    }, [setIsOpened, variant]);
    const hasIconWrapper = (folderStyle || theme.Components.Folder.FolderStyle) === 'icon';

    return (
      <StyledWrapper ref={ref} isOpened={isOpened} onClick={onClick}>
        <StyledLabel isOpened={isOpened} onClick={handleOnChange}>
          <StyledIconWrapper hasIconWrapper={hasIconWrapper}>
            {hasIconWrapper && (
              <StyledIconFolder>
                <Icon
                  name={theme.Components.Folder.IconWrapper}
                  color={theme.Color.Neutrals.Dark}
                />
              </StyledIconFolder>
            )}

            <StyledIcon hasIconWrapper={hasIconWrapper}>
              <Icon name={icon || 'folder'} size={'Medium'} color={theme.Color.Neutrals.Dark} />
            </StyledIcon>
          </StyledIconWrapper>

          <StyledTextWrapper hasSubTitle={!!subTitle}>
            <Text
              size={'Body'}
              color={theme.Components.Folder.Color.Title}
              truncate={true}
              showTooltip={true}
            >
              {name}
            </Text>

            {subTitle && (
              <Text size={'Small'} color={theme.Components.Folder.Color.Subtitle}>
                {subTitle}
              </Text>
            )}
          </StyledTextWrapper>

          {indicator && variant === 'collapse' && (
            <StyledChevronWrapper>
              <FolderIcon color={theme.Color.Neutrals.Dark} isOpened={isOpened} />
            </StyledChevronWrapper>
          )}
        </StyledLabel>

        {variant === 'collapse' && children && (
          <StyledContentWrapper>
            <MantineCollapse in={isOpened} transitionTimingFunction="linear">
              <StyledContent>{children}</StyledContent>
            </MantineCollapse>
          </StyledContentWrapper>
        )}
      </StyledWrapper>
    );
  })
);
